/**
 * usePartyDetailsForm Hook
 *
 * Manages the full Party Details form state, validation, and submission logic.
 * Orchestrates useAbnLookup and useEmailCheck hooks internally.
 *
 * Responsibilities:
 * - Form field state (address, phone, sector, sub-type)
 * - Auto-fill address from ABN lookup
 * - Auto-set sector from AUSTRAC type
 * - Full form validation
 * - Start registration session on submit
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAbnLookup, resolveEntityType, type LookupResult } from './useAbnLookup';
import { useEmailCheck, type SessionData } from './useEmailCheck';
import { formatABN, formatACN } from '../utils/abnAcnValidator';
import { startRegistrationSession } from '../services/registrationSessionService';
import { checkEmailExists } from '../services/c1OnboardingService';

// Re-export types that step components need
export type { SessionData };
export { resolveEntityType };

/** AUSTRAC reporting-entity sectors (Tranche 2 focus) */
export const SECTOR_OPTIONS = [
  { value: '', label: '— Select Sector —' },
  { value: 'accounting', label: 'Accounting Practices' },
  { value: 'legal', label: 'Law Practices' },
  { value: 'conveyancing', label: 'Conveyancers' },
  { value: 'real-estate', label: 'Real Estate Agents & Agencies' },
  { value: 'tsu', label: 'Trust & Company Service Providers (TSUs)' },
  { value: 'bullion_dealer', label: 'Bullion Dealer' },
  { value: 'other', label: 'Other (AFSL holders, ADIs, and other AUSTRAC-enrolled entities)' },
];

/** Sub-options when sector is "Other" (C1 reporting entities outside Tranche 2) */
export const OTHER_SECTOR_SUB_OPTIONS = [
  { value: '', label: '— Select Entity Type —' },
  { value: 'banking_adi', label: 'Banking and deposit-taking institutions — banks, building societies, credit unions' },
  { value: 'non_bank_lender', label: 'Non-bank lenders and finance companies' },
  { value: 'financial_planning', label: 'Financial Planning' },
  { value: 'forex_remittance', label: 'Foreign exchange and remittance services — currency exchange, money transfer businesses' },
  { value: 'securities_derivatives', label: 'Securities and derivatives dealers — stockbrokers, futures dealers, margin lenders' },
  { value: 'managed_investment', label: 'Managed investment scheme operators / responsible entities' },
  { value: 'superannuation', label: 'Superannuation trustees' },
  { value: 'life_insurance', label: 'Life insurance companies and intermediaries' },
  { value: 'digital_currency', label: 'Digital currency exchange providers' },
  { value: 'bullion', label: 'Bullion dealers' },
  { value: 'casino_wagering', label: 'Casinos and wagering/betting service providers' },
  { value: 'other_specify', label: 'Other' },
];

/**
 * Map KYB austracType values to SECTOR_OPTIONS values.
 */
export const AUSTRAC_TYPE_TO_SECTOR: Record<string, string> = {
  'Accounting Practice': 'accounting',
  'Accountant': 'accounting',
  'Auditor': 'accounting',
  'Law Practice': 'legal',
  'Lawyer': 'legal',
  'Conveyancer': 'conveyancing',
  'Legal Practice': 'legal',
  'Real Estate Agent': 'real-estate',
  'Real Estate Agency': 'real-estate',
  'Real Estate': 'real-estate',
  'Trust and Company Service Provider': 'tsu',
  'Trust & Company Service Provider': 'tsu',
  'TCSP': 'tsu',
};

export interface PartyDetailsFormData {
  partyName: string;
  partyAbn: string;
  partyAcn: string;
  partyEmail: string;
  partyEntityType: string;
  partyAddress: string;
  partySector: string;
  partySectorOther: string;
  adminPhone: string;
  adminFirstName: string;
  adminLastName: string;
}

export interface UsePartyDetailsFormOptions {
  /** Existing signup data to pre-populate fields */
  initialData?: {
    partyAbn?: string;
    partyAddress?: string;
    partyEmail?: string;
    adminPhone?: string;
    partySector?: string;
    partySectorOther?: string;
    adminFirstName?: string;
    adminLastName?: string;
  };
  /** Callback when a previous session is found and user wants to restore */
  onSessionFound?: (session: SessionData) => void;
}

export interface UsePartyDetailsFormReturn {
  // ABN/ACN lookup (delegated)
  abnLookup: ReturnType<typeof useAbnLookup>;

  // Email check (delegated)
  emailCheck: ReturnType<typeof useEmailCheck>;

  // Form fields
  partyAddress: string;
  setPartyAddress: (v: string) => void;
  partyEmail: string;
  setPartyEmail: (v: string) => void;
  adminPhone: string;
  setAdminPhone: (v: string) => void;
  partySector: string;
  setPartySector: (v: string) => void;
  partySectorOther: string;
  setPartySectorOther: (v: string) => void;
  otherSubType: string;
  setOtherSubType: (v: string) => void;
  adminFirstName: string;
  setAdminFirstName: (v: string) => void;
  adminLastName: string;
  setAdminLastName: (v: string) => void;

  // Validation
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  validateForm: () => boolean;

  // Submission
  handleSubmit: () => Promise<PartyDetailsFormData | null>;
}

export function usePartyDetailsForm(options: UsePartyDetailsFormOptions = {}): UsePartyDetailsFormReturn {
  const { initialData = {}, onSessionFound } = options;

  // Composed hooks
  const abnLookup = useAbnLookup({ initialValue: initialData.partyAbn || '' });
  const emailCheck = useEmailCheck({
    savedEmail: initialData.partyEmail,
    onSessionFound,
  });

  // Form fields
  const [partyAddress, setPartyAddress] = useState(initialData.partyAddress || '');
  const [partyEmail, setPartyEmail] = useState(initialData.partyEmail || '');
  const [adminPhone, setAdminPhone] = useState(initialData.adminPhone || '');
  const [partySector, setPartySector] = useState(initialData.partySector || '');
  const [partySectorOther, setPartySectorOther] = useState(initialData.partySectorOther || '');
  const [otherSubType, setOtherSubType] = useState('');
  const [adminFirstName, setAdminFirstName] = useState(initialData.adminFirstName || '');
  const [adminLastName, setAdminLastName] = useState(initialData.adminLastName || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-fill address and sector from lookup result
  useEffect(() => {
    if (abnLookup.lookupResult) {
      if (abnLookup.lookupResult.address) {
        setPartyAddress(abnLookup.lookupResult.address);
      }
      const detectedSector = abnLookup.lookupResult.austracType
        ? AUSTRAC_TYPE_TO_SECTOR[abnLookup.lookupResult.austracType]
        : undefined;
      if (detectedSector && !partySector) {
        setPartySector(detectedSector);
      }
    }
  }, [abnLookup.lookupResult]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync ABN lookup error into the errors map
  useEffect(() => {
    if (abnLookup.error) {
      setErrors((prev) => ({ ...prev, abnAcn: abnLookup.error }));
    } else {
      setErrors((prev) => {
        const { abnAcn: _, ...rest } = prev;
        return rest;
      });
    }
  }, [abnLookup.error]);

  // Sync email check error into the errors map
  useEffect(() => {
    if (emailCheck.emailError) {
      setErrors((prev) => ({ ...prev, partyEmail: emailCheck.emailError }));
    } else {
      setErrors((prev) => {
        const { partyEmail: _, ...rest } = prev;
        return rest;
      });
    }
  }, [emailCheck.emailError]);

  // Aggressive duplicate check while the user types. Kept in refs so this effect only re-runs
  // when {@code partyEmail} actually changes — not on every re-render caused by state that
  // {@code checkDuplicate} itself triggers (which would loop the timer endlessly).
  const checkDuplicateRef = useRef(emailCheck.checkDuplicate);
  useEffect(() => { checkDuplicateRef.current = emailCheck.checkDuplicate; }, [emailCheck.checkDuplicate]);
  const lastCheckedEmailRef = useRef<string>('');
  useEffect(() => {
    const trimmed = partyEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return;
    // Avoid a check on first render for a resumed email — we do not yet have any indication it
    // is a duplicate, and re-hitting the endpoint on session restore is wasteful.
    if (trimmed === (initialData.partyEmail || '').trim().toLowerCase()) return;
    // Skip if the same email was already checked since the last edit — no keystrokes have changed
    // the value in a meaningful way.
    if (trimmed === lastCheckedEmailRef.current) return;

    const timeoutId = window.setTimeout(() => {
      lastCheckedEmailRef.current = trimmed;
      void checkDuplicateRef.current(trimmed);
    }, 400);
    return () => window.clearTimeout(timeoutId);
  }, [partyEmail, initialData.partyEmail]);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!abnLookup.digits) {
      newErrors.abnAcn = 'ABN or ACN is required';
    } else if (abnLookup.digits.length !== 11 && abnLookup.digits.length !== 9) {
      newErrors.abnAcn = 'Enter exactly 11 digits (ABN) or 9 digits (ACN)';
    }
    if (!abnLookup.lookupResult) {
      newErrors.abnAcn = newErrors.abnAcn || 'Please verify your ABN/ACN by pressing Enter or clicking Lookup';
    }
    if (!partyEmail.trim()) {
      newErrors.partyEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partyEmail)) {
      newErrors.partyEmail = 'Please enter a valid email';
    }
    if (!adminPhone.trim()) {
      newErrors.adminPhone = 'Phone is required';
    }
    if (!adminFirstName.trim()) {
      newErrors.adminFirstName = 'First name is required';
    }
    if (!adminLastName.trim()) {
      newErrors.adminLastName = 'Last name is required';
    }
    if (!partySector) {
      newErrors.partySector = 'Sector is required';
    }
    if (partySector === 'other' && !partySectorOther.trim()) {
      newErrors.partySectorOther = 'Please specify your reporting entity type';
    }
    if (partySector === 'other' && partySectorOther === 'other_specify') {
      newErrors.partySectorOther = 'Please describe your entity type';
    }
    if (partySector === 'other' && partySectorOther.startsWith('other_specify:') && !partySectorOther.slice(14).trim()) {
      newErrors.partySectorOther = 'Please describe your entity type';
    }
    if (abnLookup.lookupResult && !partyAddress.trim()) {
      newErrors.partyAddress = 'Business address is required';
    } else if (partyAddress.trim().length > 60) {
      newErrors.partyAddress = 'Business address must be 60 characters or fewer';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [abnLookup.digits, abnLookup.lookupResult, partyEmail, adminPhone, adminFirstName, adminLastName, partySector, partySectorOther, partyAddress]);

  const handleSubmit = useCallback(async (): Promise<PartyDetailsFormData | null> => {
    if (!validateForm()) return null;

    // Force a fresh /onboarding/check-email hit on submit. The on-blur check via useEmailCheck
    // only fires if the user actually blurs the field — a fast typist can click Next before it
    // runs, and then the duplicate would only surface at Begin Activation via a raw onboarding
    // failure. Running the check here blocks the user at Party Details, which is the point.
    try {
      const registered = await checkEmailExists(partyEmail.trim());
      if (registered.exists) {
        const message = registered.message
          || 'This email is already registered with an existing organization. Please sign in with it, or use a different email.';
        setErrors((prev) => ({ ...prev, partyEmail: message }));
        return null;
      }
    } catch {
      // Non-blocking — checkEmailExists already swallows its own errors and returns exists=false
      // on failure. This catch is defensive against a future signature change.
    }

    const digits = abnLookup.digits;
    const type = abnLookup.inputType;

    // Start registration session
    try {
      await startRegistrationSession(partyEmail);
    } catch {
      // Continue even if session start fails
    }

    return {
      partyName: abnLookup.lookupResult?.name || '',
      partyAbn: type === 'abn' ? formatABN(digits) : (abnLookup.lookupResult?.abn ? formatABN(abnLookup.lookupResult.abn.replace(/\D/g, '')) : ''),
      partyAcn: type === 'acn' ? formatACN(digits) : (abnLookup.lookupResult?.acn ? formatACN(abnLookup.lookupResult.acn.replace(/\D/g, '')) : ''),
      partyEmail,
      partyEntityType: abnLookup.lookupResult?.businessType || '',
      partyAddress: partyAddress.trim(),
      partySector,
      partySectorOther: partySector === 'other' ? partySectorOther.trim() : '',
      adminPhone: adminPhone.trim(),
      adminFirstName: adminFirstName.trim(),
      adminLastName: adminLastName.trim(),
    };
  }, [validateForm, abnLookup, partyEmail, partyAddress, partySector, partySectorOther, adminPhone, adminFirstName, adminLastName]);

  return {
    abnLookup,
    emailCheck,
    partyAddress,
    setPartyAddress,
    partyEmail,
    setPartyEmail,
    adminPhone,
    setAdminPhone,
    partySector,
    setPartySector,
    partySectorOther,
    setPartySectorOther,
    otherSubType,
    setOtherSubType,
    adminFirstName,
    setAdminFirstName,
    adminLastName,
    setAdminLastName,
    errors,
    setErrors,
    validateForm,
    handleSubmit,
  };
}
